// HU-32a (MG-ILE-63) · tareas 32a-5 y 32a-7. La identidad con Entra ID, DETRÁS DE CONFIGURACIÓN.
//
// QUÉ DECIDE EL MODO
// ------------------
// El registro de la aplicación en Entra (TT1-1) es el que fija `VITE_ENTRA_CLIENT_ID`,
// `VITE_ENTRA_AUTHORITY` y `VITE_ENTRA_SCOPE_API` (AT S3 §5.1). Con las tres, la SPA entra por
// Microsoft (MSAL, `loginRedirect`, token silencioso antes de cada llamada). Sin ellas sigue
// como hasta hoy: la entrada simulada de desarrollo, que el backend solo acepta fuera de
// producción (doble guarda de `actor_actual`). `VITE_PERMITIR_LOGIN_SIMULADO=true` fuerza la
// entrada simulada aunque Entra esté configurado («solo en local», AT S3 §5.1).
//
// ⚠ DIFERENCIA CON EL AT, ANOTADA. El AT S3 §1.2 dice que `LoginSimulado` queda «solo cuando el
// build lo pide explícitamente». Mientras TT1-1 no exista, exigir la variable dejaría a dev sin
// forma de entrar; por eso SIN configuración de Entra la entrada simulada sigue disponible. En
// cuanto el pipeline inyecte las tres variables de Entra, la simulada desaparece sola.
//
// MSAL NO VIAJA EN EL BUNDLE PRINCIPAL. Se importa con `import()` solo en modo Entra: en el modo
// simulado no se descarga ni se ejecuta una línea de la librería.
//
// LO QUE NO SE PUEDE VERIFICAR SIN TT1-1: el viaje real a Microsoft (redirecciones, PKCE, el
// token v2 con `aud` de la API) y el cierre de sesión en el tenant. Las pruebas usan un MSAL
// simulado (`tests/identidad.test.ts`).
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

export type ConfiguracionDeEntra = {
  clientId: string;
  authority: string;
  /** El ámbito delegado de la API (Application ID URI + ámbito), p. ej. `api://…/acceso`. */
  scopeApi: string;
};

export type ModoDeIdentidad = 'entra' | 'simulado';

type Entorno = Record<string, unknown>;

const texto = (valor: unknown): string => (typeof valor === 'string' ? valor.trim() : '');

/** Las tres variables de Entra, o `null` si falta alguna (no se arma media configuración). */
export function configuracionDeEntra(entorno: Entorno = import.meta.env): ConfiguracionDeEntra | null {
  const clientId = texto(entorno.VITE_ENTRA_CLIENT_ID);
  const authority = texto(entorno.VITE_ENTRA_AUTHORITY);
  const scopeApi = texto(entorno.VITE_ENTRA_SCOPE_API);
  return clientId && authority && scopeApi ? { clientId, authority, scopeApi } : null;
}

/** `entra` con la configuración completa y sin el permiso explícito de entrada simulada. */
export function modoDeIdentidad(entorno: Entorno = import.meta.env): ModoDeIdentidad {
  if (!configuracionDeEntra(entorno)) return 'simulado';
  return texto(entorno.VITE_PERMITIR_LOGIN_SIMULADO) === 'true' ? 'simulado' : 'entra';
}

// ---------------------------------------------------------------------------------
// El cliente de MSAL: uno por pestaña, creado e inicializado una sola vez.
// ---------------------------------------------------------------------------------

let cliente: Promise<IPublicClientApplication> | null = null;

function clienteDeEntra(): Promise<IPublicClientApplication> {
  const configuracion = configuracionDeEntra();
  if (!configuracion) return Promise.reject(new Error('La identidad con Entra ID no está configurada.'));
  cliente ??= (async () => {
    const { PublicClientApplication } = await import('@azure/msal-browser');
    const origen = window.location.origin;
    const pca = new PublicClientApplication({
      auth: {
        clientId: configuracion.clientId,
        authority: configuracion.authority,
        redirectUri: origen,
        postLogoutRedirectUri: origen,
      },
      // `sessionStorage`: la caché muere con la pestaña, como la sesión simulada (AT S3 §1.2).
      cache: { cacheLocation: 'sessionStorage' },
    });
    await pca.initialize();
    return pca;
  })();
  return cliente;
}

/** Solo para las pruebas: olvida el cliente para que la siguiente llamada lo vuelva a crear. */
export function olvidarClienteDeEntra(): void {
  cliente = null;
}

const ambitos = (): string[] => {
  const configuracion = configuracionDeEntra();
  return configuracion ? [configuracion.scopeApi] : [];
};

/** La cuenta con la que se trabaja: la activa o, si MSAL aún no la fijó, la única de la caché. */
function cuentaDe(pca: IPublicClientApplication): AccountInfo | null {
  const activa = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
  if (activa && !pca.getActiveAccount()) pca.setActiveAccount(activa);
  return activa;
}

/**
 * Al cargar la aplicación: procesa la vuelta de Microsoft (si se viene de allí) y dice si hay
 * cuenta. `recienIniciada` es verdadero solo en la vuelta de `loginRedirect`: es cuando se
 * llama a `POST /sesion/inicio` (HU-32a-C6), una vez y no en cada recarga.
 *
 * `navigateToLoginRequestUrl`: MSAL devuelve a la página desde la que se salió, con su
 * `?vista=&caso=` (HU-32a-C5, BUG-370): el borrador retomado reaparece.
 */
export async function prepararIdentidad(): Promise<{ hayCuenta: boolean; recienIniciada: boolean }> {
  const pca = await clienteDeEntra();
  const resultado = await pca.handleRedirectPromise({ navigateToLoginRequestUrl: true });
  if (resultado?.account) pca.setActiveAccount(resultado.account);
  return { hayCuenta: Boolean(cuentaDe(pca)), recienIniciada: Boolean(resultado?.account) };
}

/** «Ingresar con mi cuenta» (HU-32a-C1). Sale a Microsoft y vuelve a esta misma página. */
export async function ingresarConEntra(): Promise<void> {
  const pca = await clienteDeEntra();
  await pca.loginRedirect({ scopes: ambitos(), redirectStartPage: window.location.href });
}

/**
 * EL TOKEN DE CADA PETICIÓN (`api/cliente.ts`). `null` en modo simulado: la petición sale sin
 * cabecera, como hasta hoy. En modo Entra, `acquireTokenSilent` renueva solo lo que pueda; si
 * Microsoft pide interacción (sesión caducada), devuelve `null` y el cliente lo trata como 401:
 * la pantalla ofrece «Volver a ingresar» (HU-32a-C5) en vez de lanzar una redirección sin aviso.
 */
export async function tokenDeAcceso(): Promise<string | null> {
  if (modoDeIdentidad() !== 'entra') return null;
  const pca = await clienteDeEntra();
  const cuenta = cuentaDe(pca);
  if (!cuenta) return null;
  try {
    const resultado = await pca.acquireTokenSilent({ scopes: ambitos(), account: cuenta });
    return resultado.accessToken || null;
  } catch {
    return null;
  }
}

/** «Volver a ingresar» del aviso de sesión caducada (HU-32a-C5): conserva la ruta en la que se estaba. */
export async function renovarSesionEnEntra(): Promise<void> {
  const pca = await clienteDeEntra();
  await pca.acquireTokenRedirect({
    scopes: ambitos(),
    account: cuentaDe(pca) ?? undefined,
    redirectStartPage: window.location.href,
  });
}

/** El cierre de sesión real (HU-32a-C8). El borrado de lo local lo hace antes `lib/sesion.ts`. */
export async function salirDeEntra(): Promise<void> {
  const pca = await clienteDeEntra();
  await pca.logoutRedirect({ account: cuentaDe(pca) ?? undefined, postLogoutRedirectUri: window.location.origin });
}
